import React, { useState, useEffect } from 'react';
import { Button, Modal, Input, Card, List, Popconfirm, Spin, Tag, message, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, GlobalOutlined } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import ImageUploader from '../../Ui/ImageUploader';
import { getImg } from '../../../Api/getImg';

const { TextArea } = Input;

const NewsScreen = () => {
    // State declarations
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [editingNews, setEditingNews] = useState(null);
    const [viewingNews, setViewingNews] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        try {
            setLoading(true);
            const res = await drEasyApi.get("/api/CenterNews/GetMyNews");
            console.log('Fetched news:', res);
            setNews(res.data || []);
        } catch (err) {
            console.error('Error fetching news:', err);
            message.error(err?.response?.data?.[0] || 'Failed to fetch news');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        form.resetFields();
        setEditingNews(null);
    };

    const handleAdd = () => {
        resetForm();
        setIsModalVisible(true);
    };

    const handleEdit = (newsItem) => {
        setEditingNews(newsItem);
        form.setFieldsValue({
            title: newsItem.title,
            titleAr: newsItem.titleAr,
            description: newsItem.description,
            descriptionAr: newsItem.descriptionAr,
            photoID: newsItem.photoID,
        });
        setIsModalVisible(true);
    };

    const handleViewDetails = (newsItem) => {
        setViewingNews(newsItem);
        setIsViewModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await drEasyApi.delete(`/api/CenterNews/Delete?Id=${id}`);
            message.success('News deleted successfully');
            fetchNews();
        } catch (err) {
            console.error('Error deleting news:', err);
            message.error(err?.response?.data?.[0] || 'Failed to delete news');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingNews) {
                await drEasyApi.put("/api/CenterNews/Edit", {
                    id: editingNews.id,
                    ...values
                });
                message.success('News updated successfully');
            } else {
                await drEasyApi.post("/api/CenterNews/Add", values);
                message.success('News created successfully');
            }

            fetchNews();
            setIsModalVisible(false);
            resetForm();
        } catch (err) {
            if (err?.errorFields) {
                message.error('Please fill all required fields');
            } else {
                console.error('Error submitting news:', err);
                message.error(err?.response?.data?.[0] || 'Failed to save news');
            }
        }
    };

    const truncateDescription = (description, maxLength = 150) => {
        if (!description || description.length <= maxLength) {
            return description;
        }
        return description.substring(0, maxLength) + '...';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="md:p-6">
            <div className="flex flex-row justify-between md:items-center mb-4">
                <h2 className="text-xl font-semibold">Center News Management</h2>
                <Button type=""
                    size="large"
                    className="bg-gradient-to-r !text-xs !p-2 md:p-4 md:!text-lg text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                    icon={<PlusOutlined />} onClick={handleAdd}>
                    Create News
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center min-h-[400px]">
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 3,
                    }}
                    dataSource={news}
                    renderItem={(item) => (
                        <List.Item>
                            <Card
                                className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg"
                                cover={
                                    item.photoID && (
                                        <div className="bg-[#f6fcfd] p-2 flex justify-center items-center h-60">
                                            <img
                                                src={getImg(item.photoID)}
                                                alt="News"
                                                className="max-h-full w-full object-contain rounded"
                                            />
                                        </div>
                                    )
                                }
                                actions={[
                                    <Button
                                        icon={<EyeOutlined />}
                                        onClick={() => handleViewDetails(item)}
                                        type="text"
                                        key="view"
                                    />,
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => handleEdit(item)}
                                        type="text"
                                        key="edit"
                                    />,
                                    <Popconfirm
                                        title="Are you sure to delete this news?"
                                        onConfirm={() => handleDelete(item.id)}
                                        key="delete"
                                    >
                                        <Button danger icon={<DeleteOutlined />} type="text" />
                                    </Popconfirm>,
                                ]}
                            >


                                <Card.Meta
                                    title={
                                        <div className="text-lg font-semibold text-[#298d9f]">
                                            {item.title}
                                        </div>
                                    }
                                    description={
                                        <div>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {truncateDescription(item.description)}
                                            </p>

                                            {item.description && item.description.length > 150 && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => handleViewDetails(item)}
                                                    className="p-0 mt-2 underline text-[#298d9f] hover:text-[#1f6b7a]"
                                                >
                                                    Read more
                                                </Button>
                                            )}
                                        </div>
                                    }
                                />
                            </Card>
                        </List.Item>
                    )}
                />
            )}

            {/* Create/Edit Modal */}
            <Modal
                title={editingNews ? 'Edit News' : 'Create News'}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    resetForm();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setIsModalVisible(false);
                        resetForm();
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
                    >
                        {editingNews ? 'Update' : 'Create'}
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                <Form form={form} layout="vertical" className="mt-4">
                    <Form.Item
                        name="title"
                        label="Title (English)"
                        rules={[{ required: true, message: 'Please enter title' }]}
                    >
                        <Input placeholder="e.g., New Medical Equipment Arrival" />
                    </Form.Item>

                    <Form.Item
                        name="titleAr"
                        label="Title (Arabic)"
                    >
                        <Input placeholder="العنوان بالعربية" dir="rtl" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description (English)"
                        rules={[{ required: true, message: 'Please enter description' }]}
                    >
                        <TextArea rows={4} placeholder="Write your news content..." />
                    </Form.Item>

                    <Form.Item
                        name="descriptionAr"
                        label="Description (Arabic)"
                    >
                        <TextArea rows={4} placeholder="اكتب محتوى الخبر..." dir="rtl" />
                    </Form.Item>

                    <Form.Item
                        name="photoID"
                        label="News Image"
                        rules={[{ required: true, message: 'Please upload an image' }]}
                    >
                        <ImageUploader form={form} value={form.getFieldValue("photoID")} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Details Modal */}
            <Modal
                title="News Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                {viewingNews && (
                    <div>


                        {viewingNews.photoID && (
                            <div className="mb-4 h-64 bg-gray-100 flex items-center justify-center rounded-lg overflow-hidden">
                                <img
                                    src={getImg(viewingNews.photoID)}
                                    alt="News"
                                    className="max-h-full w-full object-contain"
                                />
                            </div>
                        )}

                        <div className="mb-4">
                            <h3 className="text-xl font-semibold text-[#298d9f] mb-2">
                                {viewingNews.title}
                            </h3>
                            <div className="text-gray-700">
                                <p className="text-sm leading-relaxed whitespace-pre-wrap bg-gray-50 p-3 rounded">
                                    {viewingNews.description}
                                </p>
                            </div>
                        </div>

                        {viewingNews.titleAr && (
                            <div className="mb-4 border-t pt-4">
                                <h3 className="text-xl font-semibold text-[#298d9f] mb-2" dir="rtl">
                                    {viewingNews.titleAr}
                                </h3>
                                <div className="text-gray-700" dir="rtl">
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap bg-gray-50 p-3 rounded">
                                        {viewingNews.descriptionAr}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default NewsScreen;